import { IPublicMaterialPricing, IPublicPricing, IPublicRetailRange } from "../../services/pricing_public/pricing_public";

// Mirrors the backend math (order-handler royalty_basis.ts, calculate_earning.ts,
// compute_reference_total.ts, utilities/sqft.ts) step for step, including where it
// rounds, so the examples match what a real order of that size would bill and pay.

export interface WallPreset {
    id: string;
    label: string;
    widthFt: number;
    heightFt: number;
}

export const WALL_PRESETS: WallPreset[] = [
    { id: 'accent', label: 'Accent wall', widthFt: 10, heightFt: 8 },
    { id: 'feature', label: 'Feature wall', widthFt: 14, heightFt: 10 },
    { id: 'statement', label: 'Statement wall', widthFt: 20, heightFt: 12 }
];

export interface WallGeometry {
    panels: number;
    panelHeightFt: number;
    sqft: number;
}

export interface WallEstimate extends WallGeometry {
    basis: number;
    gross: number;
    fee: number;
    net: number;
    /** What printing this wall costs a brand on each pricing tier. */
    artistCost: number;
    storeOwnerCost: number;
}

export interface StoreOrderEstimate extends WallGeometry {
    revenue: number;
    materialCost: number;
    fulfillmentFee: number;
    orderProcessing: number;
    profit: number;
    /** The same order's profit on Artist pricing, for contrast. */
    artistProfit: number;
}

type GeometryPricing = Pick<IPublicPricing, 'panel_width_inches'>;

const round2 = (n: number): number => Math.round(n * 100) / 100;

// #region wallGeometry
/** Panels are 19in wide and come in even-foot heights, so a wall rounds up to both. */
export function wallGeometry(wall: WallPreset, pricing: GeometryPricing): WallGeometry {
    const panels = Math.ceil((wall.widthFt * 12) / pricing.panel_width_inches);
    const panelHeightFt = Math.max(2, Math.ceil(wall.heightFt / 2) * 2);
    return { panels, panelHeightFt, sqft: round2(panelHeightFt * (pricing.panel_width_inches / 12) * panels) };
}

// #region estimateWall
/** One order for a whole wall, licensed at `royaltyPercent`. */
export function estimateWall(
    wall: WallPreset,
    material: IPublicMaterialPricing,
    royaltyPercent: number,
    pricing: GeometryPricing & Pick<IPublicPricing, 'licensing_fee'>
): WallEstimate {
    const geometry = wallGeometry(wall, pricing);
    const basisCents = Math.round(round2(geometry.sqft * material.royalty_basis_sqft) * 100);
    const grossCents = royaltyPercent > 0 ? Math.round((basisCents * royaltyPercent) / 100) : 0;
    const feeCents = pricing.licensing_fee.type === 'FLAT'
        ? Math.min(Math.round(pricing.licensing_fee.value * 100), grossCents)
        : Math.round((grossCents * pricing.licensing_fee.value) / 100);

    return {
        ...geometry,
        basis: basisCents / 100,
        gross: grossCents / 100,
        fee: feeCents / 100,
        net: (grossCents - feeCents) / 100,
        artistCost: round2(geometry.sqft * material.artist_cost_sqft),
        storeOwnerCost: round2(geometry.sqft * material.store_owner_cost_sqft)
    };
}

// #region estimateStoreOrder
/**
 * One order for a whole wall of the brand's own design, sold in its own store at
 * `retailSqft`. No royalty is owed on your own design. Shipping and boxes are left
 * out: they depend on the destination and are usually charged to the customer.
 */
export function estimateStoreOrder(
    wall: WallPreset,
    material: IPublicMaterialPricing,
    retailSqft: number,
    pricing: GeometryPricing & Pick<IPublicPricing, 'store_owner_fees'>
): StoreOrderEstimate {
    const geometry = wallGeometry(wall, pricing);
    const revenue = round2(geometry.sqft * retailSqft);
    const fulfillmentFee = round2(round2(geometry.sqft * material.royalty_basis_sqft) * pricing.store_owner_fees.fulfillment_fee_percent / 100);
    const orderProcessing = pricing.store_owner_fees.order_processing;
    const materialCost = round2(geometry.sqft * material.store_owner_cost_sqft);
    const artistCost = round2(geometry.sqft * material.artist_cost_sqft);

    return {
        ...geometry,
        revenue,
        materialCost,
        fulfillmentFee,
        orderProcessing,
        profit: round2(revenue - materialCost - fulfillmentFee - orderProcessing),
        artistProfit: round2(revenue - artistCost - fulfillmentFee - orderProcessing)
    };
}

// #region retailSliderRange
/**
 * The slider's bounds and starting point for one material. Uses what brands charge
 * today when the API has it; otherwise a span around the Artist cost.
 */
export function retailSliderRange(material: IPublicMaterialPricing, range?: IPublicRetailRange): { min: number; max: number; start: number } {
    if (range) return { min: Math.floor(range.low), max: Math.ceil(range.high), start: range.typical };
    const base = Math.ceil(material.artist_cost_sqft);
    return { min: base, max: base * 3, start: base * 2 };
}

// #region biggestSaving
/** The largest Store Owner discount across materials, as a whole percent. */
export function biggestSaving(materials: IPublicMaterialPricing[]): number {
    const savings = materials
        .filter((m) => m.artist_cost_sqft > 0)
        .map((m) => (m.artist_cost_sqft - m.store_owner_cost_sqft) / m.artist_cost_sqft);
    return savings.length ? Math.round(Math.max(...savings) * 100) : 0;
}

// #region money
export const money = (n: number, cents = true): string =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
